import {
  createUnifiedTheme,
  genPageTheme,
  shapes,
  palettes,
} from '@backstage/theme';

// ── Tema ODPTech (design system) ─────────────────────────────────────────────
// Paleta do logotipo odptech:
//   laranja  #e2571e  (primária / ação)
//   teal     #17a2b8  (secundária / accent)   teal-petróleo #0d5457 (o "p")
//   grafite  #2b2b2b / #454545 ("tech")       fundo claro   #f4f5f6

// ── Tokens claros ────────────────────────────────────────────────────────────
const ODP_ORANGE = '#e2571e';
const ODP_ORANGE_DARK = '#a03c11';
const ODP_ORANGE_LIGHT = '#ef7a4a';
const ODP_TEAL = '#17a2b8';
const ODP_TEAL_DARK = '#0d5457';
const ODP_TEAL_LIGHT = '#3cbcd0';
const ODP_DARK = '#2b2b2b';
const ODP_GRAY = '#454545';
const ODP_BG = '#f4f5f6';
const ODP_SURFACE = '#ffffff';
const ODP_NAV = '#23232a'; // sidebar grafite (identidade "tech")

// ── Tokens dark ──────────────────────────────────────────────────────────────
const DARK_BG = '#161618';
const DARK_SURFACE = '#1f1f22';
const DARK_SURFACE2 = '#2a2a2d';
const DARK_NAV = '#121214';
const DARK_TEXT = '#f0f0f2';
const DARK_TEXT2 = '#a0a0a6';

const brandGradient = `linear-gradient(135deg, ${ODP_TEAL_DARK} 0%, ${ODP_TEAL} 55%, ${ODP_ORANGE} 100%)`;

export const odptechTheme = createUnifiedTheme({
  palette: {
    ...palettes.light,
    mode: 'light',
    primary: {
      main: ODP_ORANGE,
      light: ODP_ORANGE_LIGHT,
      dark: ODP_ORANGE_DARK,
      contrastText: '#ffffff',
    },
    secondary: {
      main: ODP_TEAL,
      light: ODP_TEAL_LIGHT,
      dark: ODP_TEAL_DARK,
      contrastText: '#ffffff',
    },
    background: {
      default: ODP_BG,
      paper: ODP_SURFACE,
    },
    text: {
      primary: ODP_DARK,
      secondary: ODP_GRAY,
    },
    navigation: {
      background: ODP_NAV,
      indicator: ODP_ORANGE,
      color: '#c9c9cf',
      selectedColor: '#ffffff',
      navItem: {
        hoverBackground: 'rgba(226, 87, 30, 0.20)',
      },
      submenu: {
        background: '#1c1c1e',
      },
    },
    status: {
      ok: '#1db954',
      warning: ODP_ORANGE,
      error: '#e53935',
      running: ODP_TEAL,
      pending: '#ffc107',
      aborted: ODP_GRAY,
    },
    bursts: {
      fontColor: '#ffffff',
      slackChannelText: '#ffe6d9',
      backgroundColor: {
        default: ODP_ORANGE,
      },
      gradient: {
        linear: brandGradient,
      },
    },
    pinSidebarButton: {
      icon: '#c9c9cf',
      background: '#1c1c1e',
    },
    tabbar: {
      indicator: ODP_ORANGE,
    },
    link: ODP_TEAL_DARK,
    linkHover: ODP_ORANGE,
    gold: ODP_ORANGE,
    highlight: '#fdefe8',
    errorText: '#e53935',
    infoText: ODP_TEAL,
    warningText: ODP_ORANGE,
    errorBackground: '#fdecea',
    warningBackground: '#fdefe8',
    infoBackground: '#e6f6f9',
  },
  defaultPageTheme: 'home',
  fontFamily: 'Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
  components: {
    MuiButton: {
      styleOverrides: {
        contained: {
          borderRadius: 8,
          fontWeight: 600,
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': { boxShadow: '0 2px 8px rgba(226, 87, 30, 0.35)' },
        },
        outlined: { borderRadius: 8, textTransform: 'none', fontWeight: 600 },
        text: { textTransform: 'none', fontWeight: 500 },
      },
    },
    MuiChip: {
      styleOverrides: { root: { borderRadius: 6, fontWeight: 500 } },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 1px 4px rgba(0,0,0,0.08), 0 2px 12px rgba(0,0,0,0.06)',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: { root: { '&:hover': { backgroundColor: '#fdefe8' } } },
    },
  },
  pageTheme: {
    home: genPageTheme({ colors: [ODP_TEAL_DARK, ODP_TEAL], shape: shapes.wave }),
    documentation: genPageTheme({ colors: [ODP_TEAL, ODP_TEAL_LIGHT], shape: shapes.wave2 }),
    tool: genPageTheme({ colors: [ODP_ORANGE_DARK, ODP_ORANGE], shape: shapes.round }),
    service: genPageTheme({ colors: [ODP_TEAL_DARK, ODP_TEAL], shape: shapes.wave }),
    website: genPageTheme({ colors: [ODP_TEAL, ODP_ORANGE], shape: shapes.wave }),
    library: genPageTheme({ colors: [ODP_TEAL_DARK, ODP_TEAL], shape: shapes.wave2 }),
    other: genPageTheme({ colors: [ODP_TEAL_DARK, ODP_ORANGE], shape: shapes.wave }),
    app: genPageTheme({ colors: [ODP_TEAL_DARK, ODP_TEAL], shape: shapes.wave }),
    apis: genPageTheme({ colors: [ODP_TEAL, ODP_ORANGE], shape: shapes.wave2 }),
  },
});

// ── Tema Dark ────────────────────────────────────────────────────────────────
export const odptechDarkTheme = createUnifiedTheme({
  palette: {
    ...palettes.dark,
    mode: 'dark',
    primary: {
      main: ODP_ORANGE_LIGHT,
      light: '#f59d78',
      dark: ODP_ORANGE,
      contrastText: '#1c1c1e',
    },
    secondary: {
      main: ODP_TEAL_LIGHT,
      light: '#6fd3e2',
      dark: ODP_TEAL,
      contrastText: '#1c1c1e',
    },
    background: {
      default: DARK_BG,
      paper: DARK_SURFACE,
    },
    text: {
      primary: DARK_TEXT,
      secondary: DARK_TEXT2,
    },
    navigation: {
      background: DARK_NAV,
      indicator: ODP_ORANGE_LIGHT,
      color: '#9a9aa0',
      selectedColor: '#ffffff',
      navItem: {
        hoverBackground: 'rgba(239, 122, 74, 0.15)',
      },
      submenu: {
        background: '#0c0c0e',
      },
    },
    status: {
      ok: '#22c55e',
      warning: ODP_ORANGE_LIGHT,
      error: '#ef4444',
      running: ODP_TEAL_LIGHT,
      pending: '#fbbf24',
      aborted: DARK_TEXT2,
    },
    bursts: {
      fontColor: '#ffffff',
      slackChannelText: '#f59d78',
      backgroundColor: {
        default: DARK_SURFACE2,
      },
      gradient: {
        linear: `linear-gradient(135deg, ${ODP_TEAL_DARK} 0%, ${ODP_TEAL} 55%, ${ODP_ORANGE} 100%)`,
      },
    },
    pinSidebarButton: {
      icon: '#9a9aa0',
      background: '#0c0c0e',
    },
    tabbar: {
      indicator: ODP_ORANGE_LIGHT,
    },
    link: ODP_TEAL_LIGHT,
    linkHover: ODP_ORANGE_LIGHT,
    gold: ODP_ORANGE_LIGHT,
    highlight: DARK_SURFACE2,
    errorText: '#fca5a5',
    infoText: ODP_TEAL_LIGHT,
    warningText: ODP_ORANGE_LIGHT,
    errorBackground: '#2d1515',
    warningBackground: '#2d1a0d',
    infoBackground: '#0d2a2e',
  },
  defaultPageTheme: 'home',
  fontFamily: 'Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
  components: {
    MuiButton: {
      styleOverrides: {
        contained: {
          borderRadius: 8,
          fontWeight: 600,
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': { boxShadow: '0 2px 12px rgba(239, 122, 74, 0.4)' },
        },
        outlined: { borderRadius: 8, textTransform: 'none', fontWeight: 600 },
        text: { textTransform: 'none', fontWeight: 500 },
      },
    },
    MuiChip: {
      styleOverrides: { root: { borderRadius: 6, fontWeight: 500 } },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundImage: 'none',
          boxShadow: '0 1px 3px rgba(0,0,0,0.4), 0 4px 16px rgba(0,0,0,0.3)',
          border: `1px solid ${DARK_SURFACE2}`,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: { root: { '&:hover': { backgroundColor: DARK_SURFACE2 } } },
    },
  },
  pageTheme: {
    home: genPageTheme({ colors: [DARK_NAV, ODP_TEAL_DARK], shape: shapes.wave }),
    documentation: genPageTheme({ colors: [ODP_TEAL_DARK, ODP_TEAL], shape: shapes.wave2 }),
    tool: genPageTheme({ colors: ['#7d2f0e', ODP_ORANGE], shape: shapes.round }),
    service: genPageTheme({ colors: [DARK_NAV, ODP_TEAL_DARK], shape: shapes.wave }),
    website: genPageTheme({ colors: [ODP_TEAL_DARK, ODP_ORANGE], shape: shapes.wave }),
    library: genPageTheme({ colors: [DARK_NAV, ODP_TEAL_DARK], shape: shapes.wave2 }),
    other: genPageTheme({ colors: [DARK_NAV, ODP_ORANGE], shape: shapes.wave }),
    app: genPageTheme({ colors: [DARK_NAV, ODP_TEAL_DARK], shape: shapes.wave }),
    apis: genPageTheme({ colors: [ODP_TEAL, ODP_ORANGE_LIGHT], shape: shapes.wave2 }),
  },
});
